import React from 'react';
// Version check test trigger
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalInstance } from './services/authConfig';
import { PermissionProvider } from './contexts/PermissionContext';
import { PermissionsModeProvider } from './contexts/PermissionsModeContext';
import { CustomizeLayoutModeProvider } from './contexts/CustomizeLayoutModeContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { IconLegendProvider } from './contexts/IconLegendContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { PresentationModeProvider } from './contexts/PresentationModeContext';
import { DisplayPreferencesProvider } from './contexts/DisplayPreferencesContext';
import { NotificationPreferencesProvider } from './contexts/NotificationPreferencesContext';
import { GlobalFiltersProvider } from './contexts/GlobalFiltersContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeEngineProvider } from './theme/ThemeEngineContext';
import { FieldDefinitionsProvider } from './contexts/FieldDefinitionsContext';
import { WorkingStatusPreferenceProvider } from './contexts/WorkingStatusPreferenceContext';
import { OrderFormProvider } from './contexts/OrderFormContext';
import { ProductionReadonlyModeProvider } from './contexts/ProductionReadonlyModeContext';
import { QuoteDrawerProvider } from './contexts/QuoteDrawerProvider';
import ProductionReadonlyToast from './components/ProductionReadonlyToast';
import PermissionDeniedToast from './components/PermissionDeniedToast';
import ForceLogoutBanner from './components/ForceLogoutBanner';
import { useVersionCheck } from './hooks/useVersionCheck';
import userSettingsService from './services/userSettings.service';
import Login from './components/Login';
import DashboardLayout from './components/DashboardLayout';
import PrivateRoute from './components/PrivateRoute';
import GuardLayout from './components/GuardLayout';
import GuardPrivateRoute from './components/GuardPrivateRoute';
import YardReeferLog from './pages/yard/YardReeferLog';
import YardInventory from './pages/yard/YardInventory';
import YardReports from './pages/yard/YardReports';
import ChangePassword from './pages/yard/ChangePassword';
import BugReportPopout from './pages/popout/BugReportPopout';
import FeatureRequestPopout from './pages/popout/FeatureRequestPopout';
import HelpdeskTicketPopout from './pages/popout/HelpdeskTicketPopout';
import { useGuardAuthenticated } from './hooks/useGuardAuthenticated';
import { buildPageTitle } from './utils/pageTitle';
import './App.css';

function VersionChecker() {
  const { newVersionAvailable, refreshPage } = useVersionCheck(60000); // Check every 60 seconds
  const { showToast } = useToast();
  const [toastShown, setToastShown] = React.useState(false);

  React.useEffect(() => {
    if (newVersionAvailable && !toastShown) {
      showToast({
        type: 'info',
        message: 'A new version is available! Click to refresh.',
        duration: 0, // Persistent until dismissed
        onClick: refreshPage,
      });
      setToastShown(true);
    }
  }, [newVersionAvailable, toastShown, showToast, refreshPage]);

  return null;
}

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const isGuardAuthenticated = useGuardAuthenticated();

  // SECURITY FIX: Auth bypass only works in development mode
  // Even if VITE_BYPASS_AUTH is set, it's ignored in production builds
  const isDevelopment = import.meta.env.DEV;
  const bypassAuth = isDevelopment && import.meta.env.VITE_BYPASS_AUTH === 'true';

  // Set the initial/base page title (per-page titles inside the app are managed
  // by DashboardLayout's navigation; this covers login, guard, and popout views).
  React.useEffect(() => {
    document.title = buildPageTitle(null, window.location.pathname);
  }, []);

  // Check if we're handling a redirect
  React.useEffect(() => {
    // Handle redirect response from Azure AD
    if (!bypassAuth) {
      instance.handleRedirectPromise()
        .then((response) => {
          if (response && response.account) {
            instance.setActiveAccount(response.account);
          }
        })
        .catch((error) => {
          console.error('Redirect error:', error);
        });
    }
  }, [instance, bypassAuth]);

  // Load user settings after authentication (on mount and when auth changes)
  React.useEffect(() => {
    const loadUserSettings = async () => {
      if (!isAuthenticated && !bypassAuth) return;

      // Only load if not already in localStorage
      const existingValue = localStorage.getItem('live_map_icon_size');
      if (existingValue) {
        // console.log('Icon size already in localStorage:', existingValue);
        return;
      }

      // console.log('Loading icon size from API on app mount...');
      try {
        // Load icon size preference for LiveMap
        const iconSizeSetting = await userSettingsService.getUserSetting('live_map_icon_size');
        if (iconSizeSetting && iconSizeSetting.setting_value && typeof iconSizeSetting.setting_value.size === 'number') {
          localStorage.setItem('live_map_icon_size', iconSizeSetting.setting_value.size.toString());
          // console.log('Loaded icon size from API:', iconSizeSetting.setting_value.size);
        } else {
          // Set default if no preference exists
          localStorage.setItem('live_map_icon_size', '1');
          console.log('No saved preference, using default: 1');
        }
      } catch (error: any) {
        // Only log non-404 errors (404 is expected for new users)
        if (error?.response?.status !== 404) {
          console.error('Failed to load user settings:', error);
        }
        // Set default on error
        localStorage.setItem('live_map_icon_size', '1');
      }
    };

    loadUserSettings();
  }, [isAuthenticated, bypassAuth]);

  // Wait for MSAL to finish initializing or handling redirect (unless bypassing auth)
  if (!bypassAuth && (inProgress === 'startup' || inProgress === 'handleRedirect')) {
    return <div>Loading...</div>;
  }

  return (
    // Outermost: Presentation Mode owns a body-level transform and depends on
    // nothing, and its hotkey has to work no matter what is mounted below.
    <PresentationModeProvider>
    <ToastProvider>
      <VersionChecker />
      <ProductionReadonlyToast />
      <PermissionDeniedToast />
      <ForceLogoutBanner />
      <ImpersonationProvider>
        <ProductionReadonlyModeProvider>
          <PermissionProvider>
            <PermissionsModeProvider>
              <CustomizeLayoutModeProvider>
                {/* Above ThemeProvider and DisplayPreferencesProvider on
                    purpose: both of those write CSS vars inline on <html>, and
                    the engine's stylesheet has to be in place first so their
                    inline values keep winning where they still own a token. */}
                <ThemeEngineProvider>
                <ThemeProvider>
                  <DisplayPreferencesProvider>
                    <NotificationPreferencesProvider>
                    <GlobalFiltersProvider>
                      <WorkingStatusPreferenceProvider>
                        <OrderFormProvider>
                        <IconLegendProvider>
                          <FieldDefinitionsProvider>
                            <BrowserRouter>
                          {/* Inside the router: the drawer's sub-tabs are page-level
                              components and must not be the thing that discovers there is
                              no Router above them. */}
                          <QuoteDrawerProvider>
                          <Routes>
                            <Route path="/login" element={
                              bypassAuth || isAuthenticated ? <Navigate to="/home" replace /> : <Login />
                            } />
                            {/* Guard-only /yard/* tree. Registered only when a guard JWT is present;
                                otherwise /yard/* falls through to DashboardLayout for MSAL employees. */}
                            {isGuardAuthenticated && (
                              <Route path="/yard/*" element={
                                <GuardPrivateRoute>
                                  <GuardLayout />
                                </GuardPrivateRoute>
                              }>
                                <Route index element={<Navigate to="inventory" replace />} />
                                <Route path="reefer-log" element={<YardReeferLog />} />
                                <Route path="inventory" element={<YardInventory />} />
                                <Route path="reports" element={<YardReports />} />
                                <Route path="change-password" element={<ChangePassword />} />
                              </Route>
                            )}
                            <Route path="/popout/bug-report" element={
                              <PrivateRoute>
                                <BugReportPopout />
                              </PrivateRoute>
                            } />
                            <Route path="/popout/feature-request" element={
                              <PrivateRoute>
                                <FeatureRequestPopout />
                              </PrivateRoute>
                            } />
                            <Route path="/popout/helpdesk-ticket" element={
                              <PrivateRoute>
                                <HelpdeskTicketPopout />
                              </PrivateRoute>
                            } />
                            <Route path="/*" element={
                              <PrivateRoute>
                                <DashboardLayout />
                              </PrivateRoute>
                            } />
                          </Routes>
                          </QuoteDrawerProvider>
                            </BrowserRouter>
                          </FieldDefinitionsProvider>
                        </IconLegendProvider>
                        </OrderFormProvider>
                      </WorkingStatusPreferenceProvider>
                    </GlobalFiltersProvider>
                    </NotificationPreferencesProvider>
                  </DisplayPreferencesProvider>
                  </ThemeProvider>
                </ThemeEngineProvider>
                </CustomizeLayoutModeProvider>
              </PermissionsModeProvider>
            </PermissionProvider>
          </ProductionReadonlyModeProvider>
        </ImpersonationProvider>
      </ToastProvider>
    </PresentationModeProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MsalProvider instance={msalInstance}>
        <AppContent />
      </MsalProvider>
    </QueryClientProvider>
  );
}

export default App;